import { HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { readRows } from '../data-access/page-all';
import { PaginatedResponse, QueryParams } from '../models/pagination.model';

/**
 * Converts a plain query-params object into Angular HttpParams, skipping undefined/null.
 *
 * The app models pages as `page` (1-based) + `limit`; the Vantage backend pages with
 * `limit` + `offset`. So when `page` is present we emit `offset = (page-1)*limit` and drop
 * `page`; everything else (`search`, `sortBy`, `sortDir`, resource-specific keys) passes
 * through verbatim.
 */
export function toHttpParams(params: QueryParams): HttpParams {
  let httpParams = new HttpParams();
  // A cursor REPLACES offset — it is not sent alongside it. See the block comment below.
  const cursor = typeof params.cursor === 'string' && params.cursor.length > 0 ? params.cursor : null;
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (key === 'page') continue; // translated to offset below
    if (key === 'cursor' && !cursor) continue; // an empty cursor is not a cursor
    httpParams = httpParams.set(key, String(value));
  }
  if (cursor) return httpParams;
  const { page, limit } = params;
  if (typeof page === 'number' && typeof limit === 'number' && !httpParams.has('offset')) {
    httpParams = httpParams.set('offset', String(Math.max(0, (page - 1) * limit)));
  }
  return httpParams;
}

/**
 * The paging params for a KEYSET list request — `limit`, plus `cursor` only when there is one.
 *
 * ⚠️ **It never sends `offset`, and that is the point.** vantage-backend#881 converted 65 list
 * routes to `limit`/`cursor`, and a converted route does not DECLARE `offset`. FastAPI drops an
 * undeclared param rather than rejecting it (RBP #38), so an offset request returns **the same
 * first page forever** — same rows, HTTP 200, a Next button that looks like it works. Sending
 * `offset=0` on page 1 is equally undeclared, which is why there is no "harmless" case and no
 * fallback branch here.
 *
 * Page 1 correctly sends neither: both paging schemes start at the beginning with no positional
 * param, so the first request is identical either way and the RESPONSE reveals which scheme the
 * route uses (`normalizePagedResponse`, `readRows`).
 *
 * @param limit  the page size — the route caps it at 100 (`Query(50, ge=1, le=100)`)
 * @param cursor the previous response's `next_cursor`; absent on the first page
 */
export function keysetParams(limit: number, cursor?: string | null): HttpParams {
  const p = new HttpParams().set('limit', String(limit));
  return typeof cursor === 'string' && cursor.length > 0 ? p.set('cursor', cursor) : p;
}

/**
 * Pull the row array out of a list payload that may be a bare array or a `{ data: [] }` envelope.
 *
 * Written as guard clauses rather than a chained ternary on purpose: the nested form was flagged by
 * SonarQube in two places, and it is genuinely harder to read — the interesting case (an envelope
 * whose `data` is present but is NOT an array) hides in the middle of the chain. Anything that is
 * neither shape yields `[]`, because reading one shape as the other renders an empty screen with no
 * error (RBP #20), and an empty array at least fails visibly at the call site.
 *
 * ⚠️ Now a thin alias for {@link readRows}, which adds the keyset `{results}` envelope
 * (vantage-backend#871). This one function is what makes the six list services behind
 * {@link normalizePagedResponse} — contacts, tasks, vendor-bills, recurring-schedules, inspections,
 * service-requests — tolerant in one edit.
 *
 * Worth recording how nearly it was missed: the vantage-web#1102 audit searched for
 * `Array.isArray(raw)`, and this reads a parameter called `v`. A grep over a NAMING convention is
 * not a search over a behaviour, and this one sits behind the shared pager every header-reading list
 * uses.
 */
export function asRowArray(v: unknown): unknown[] {
  return readRows(v);
}

/**
 * Normalizes a list response into the app's {@link PaginatedResponse} shape, tolerating the
 * three forms the backend may return:
 *  - a **bare array** (current Vantage list endpoints, e.g. `GET /users`),
 *  - the backend envelope `{ items, total, page, page_size, has_next }`,
 *  - the legacy frontend envelope `{ data, total, page, limit }`,
 *  - the **keyset** envelope `{ results, next_cursor }` (vantage-backend#871), which carries no
 *    total, so the page length stands in for one as it does for a bare array.
 *
 * Pass `mapRow` to transform each raw row (e.g. snake→camel) in one place.
 */
export function normalizeListResponse<T = unknown>(
  raw: unknown,
  mapRow: (row: unknown) => T = (row) => row as T,
): PaginatedResponse<T> {
  if (Array.isArray(raw)) {
    const data = raw.map(mapRow);
    return { data, total: data.length, page: 1, limit: data.length || 50 };
  }

  if (raw && typeof raw === 'object') {
    const body = raw as Record<string, unknown>;

    if (Array.isArray(body['items'])) {
      const data = (body['items'] as unknown[]).map(mapRow);
      const limit = toNum(body['page_size'], data.length || 50);
      return { data, total: toNum(body['total'], data.length), page: toNum(body['page'], 1), limit };
    }

    if (Array.isArray(body['data'])) {
      const data = (body['data'] as unknown[]).map(mapRow);
      return {
        data,
        total: toNum(body['total'], data.length),
        page: toNum(body['page'], 1),
        limit: toNum(body['limit'], data.length || 50),
      };
    }

    // The KEYSET envelope (vantage-backend#871). It carries no total — a cursor page cannot produce
    // one without scanning past itself — so the page length stands in for one, exactly as the
    // bare-array branch above already does. `next_cursor` is not surfaced here: this return shape
    // has no field for it, and its one pager, `pageAll`, follows the cursor itself.
    if (Array.isArray(body['results'])) {
      const data = (body['results'] as unknown[]).map(mapRow);
      return { data, total: data.length, page: 1, limit: data.length || 50 };
    }
  }

  return { data: [], total: 0, page: 1, limit: 0 };
}

function toNum(v: unknown, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Normalizes a **bare-array list response** into the app's paged shape, taking the total from the
 * route's `X-Total-Count` rather than guessing it from the page length.
 *
 * Every Vantage list route computes a real total and sets the header, and the shared CORS
 * middleware exposes it to the browser (`vantage_shared/middleware.py`:
 * `expose_headers=["X-Total-Count"]`) — so the exact number is reachable and was simply not being
 * read. Without it a pager can only infer "the page came back full, so there is probably one
 * more", which renders as "1-25 of 26" on a list of 300 and is wrong on every page but the last.
 *
 * ⚠️ **Requires `observe: 'response'` on the call.** Under the default `observe: 'body'` there are
 * no headers to read, and this returns the estimate forever without failing — so the mistake looks
 * exactly like success.
 *
 * The estimate stays as the FALLBACK rather than an equal alternative: a proxy that strips the
 * header, or a fixture that never sets one, must still page. Concluding `total = 0` there would
 * hide every row after the first page — a worse failure than an imprecise count.
 *
 * An empty or blank header counts as absent for the same reason. `Number('')` is `0`, which is
 * finite, so trusting it would turn a malformed header into "no results" on a page that has them.
 */
export function normalizePagedResponse<T>(
  response: HttpResponse<unknown>,
  page: number,
  limit: number,
  mapRow: (row: unknown) => T,
): PaginatedResponse<T> {
  const data = asRowArray(response.body).map(mapRow);
  const raw = response.headers.get('X-Total-Count');
  const headerTotal = raw !== null && raw.trim() !== '' ? Number(raw) : Number.NaN;
  const estimatedTotal = (page - 1) * limit + data.length + (data.length === limit ? 1 : 0);
  const total = Number.isFinite(headerTotal) ? headerTotal : estimatedTotal;

  // Keyset (vantage-backend#877): the body is `{results, next_cursor}` and there is no
  // `X-Total-Count` to read, so `total` is the same estimate this function has always fallen back
  // to when the header does not reach us. That is deliberate: the displayed count degrades to what
  // it already shows behind a header-stripping proxy, rather than gaining a new and confident lie.
  // `nextCursor` is the authority on whether another page exists; `total` is not.
  const body = response.body;
  const env = (body && typeof body === 'object' && !Array.isArray(body) ? body : {}) as Record<string, unknown>;
  if (Array.isArray(env['results'])) {
    const next = env['next_cursor'];
    return {
      data,
      total,
      page,
      limit,
      // An empty string is not a cursor. Treating one as truthy would page forever.
      nextCursor: typeof next === 'string' && next.length > 0 ? next : null,
      cursorPaged: true,
    };
  }
  return { data, total, page, limit };
}

/**
 * Turns any thrown HTTP/Cognito/JS error into a **user-friendly** message — never raw
 * technical detail. Prefers the backend's `{ detail }` string (already human-readable), then
 * a status-specific fallback, then the error message. Log the raw error separately for devs.
 */
export function parseApiError(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (err instanceof HttpErrorResponse) {
    const body: unknown = err.error;
    const detail =
      typeof body === 'string'
        ? body
        : body && typeof body === 'object'
          ? (body as Record<string, unknown>)['detail']
          : undefined;
    if (typeof detail === 'string' && detail.trim()) return detail;

    switch (err.status) {
      case 0:
        return 'Cannot reach the server. Check your connection and try again.';
      case 401:
        return 'Your session has expired. Please sign in again.';
      case 403:
        return "You don't have permission to do that.";
      case 404:
        return 'That item could not be found.';
      case 409:
        return 'That action conflicts with the current state. Refresh and try again.';
      case 422:
        return 'Some of the information provided is invalid.';
      case 500:
      case 502:
      case 503:
        return 'The server ran into a problem. Please try again shortly.';
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** The machine-readable half of a backend refusal — `{ detail, code, ...extra }`. */
export interface ApiFailure {
  /** The backend's `code` (e.g. `VERSION_CONFLICT`), when it set one. */
  readonly code: string | null;
  /** The human-readable message, already resolved through `parseApiError`. */
  readonly message: string;
  /** HTTP status, or 0 when the request never reached the server. */
  readonly status: number;
  /** Anything else the backend merged into the body — who/when for a conflict, ids for a refusal. */
  readonly extra: Readonly<Record<string, unknown>>;
}

/** Fields a VERSION_CONFLICT may carry. All optional — older handlers send none of them. */
interface ConflictExtra {
  updated_by?: unknown;
  updated_at?: unknown;
  current_version?: unknown;
}

/**
 * Reads the structured part of a failure, not just its sentence.
 *
 * `parseApiError` returns display text, which is right for a toast and useless for a decision —
 * "conflicts with the current state" cannot be told apart from any other 409 by matching on the
 * string. The backend has emitted a machine-readable `code` since #123 (`exceptions.py`: "the
 * handful of 4xx errors the web branches on") and nothing on this side ever read it.
 */
export function parseApiFailure(err: unknown, fallback?: string): ApiFailure {
  const message = parseApiError(err, fallback);
  if (!(err instanceof HttpErrorResponse)) return { code: null, message, status: 0, extra: {} };

  const body: unknown = err.error;
  const obj = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  const code = typeof obj['code'] === 'string' ? obj['code'] : null;
  // `detail` and `code` are the envelope; everything else is the payload a refusal carries.
  const extra: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (k !== 'detail' && k !== 'code') extra[k] = v;
  }
  return { code, message, status: err.status, extra };
}

/**
 * The message for a save that lost a race, phrased so the user knows what to DO.
 *
 * "Refresh and try again" is what the generic 409 says, and it reads as "your work is gone" —
 * which is the opposite of true. Optimistic concurrency means the write was REFUSED, so nothing
 * was overwritten and nothing was lost: the form still holds their edit. The message says that
 * first, then names who got there first when the backend supplies it.
 *
 * Returns `null` when this is not a version conflict, so callers can fall through to their own
 * handling rather than mislabelling an unrelated 409 (a status-machine refusal is also a 409).
 */
export function conflictMessage(failure: ApiFailure): string | null {
  if (failure.code !== 'VERSION_CONFLICT') return null;
  const x = failure.extra as ConflictExtra;
  const who = typeof x.updated_by === 'string' && x.updated_by.trim() ? x.updated_by.trim() : null;
  const when = typeof x.updated_at === 'string' ? relativeTime(x.updated_at) : null;

  const other = who && when ? `${who} changed it ${when}` : who ? `${who} changed it` : 'Somebody else changed it';
  return (
    `${other} while you were editing, so your save was refused rather than overwriting them. ` +
    'Nothing you typed is lost — reload to see their version, then re-apply your change.'
  );
}

/** "2 minutes ago" / "just now". Deliberately coarse — the point is recency, not precision. */
function relativeTime(iso: string): string | null {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return null;
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  return null;
}
